import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useDeleteThread } from '@/hooks/useChatThreads';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';

interface DeleteThreadModalProps {
  open: boolean;
  onClose: () => void;
  threadId: string;
  onSuccess?: () => void;
}

export function DeleteThreadModal({
  open,
  onClose,
  threadId,
  onSuccess,
}: DeleteThreadModalProps) {
  const [deleteTraceOnly, setDeleteTraceOnly] = useState(false);
  const [deleteFull, setDeleteFull] = useState(false);

  const { mutateAsync: deleteThread, isPending } = useDeleteThread();
  const { error: toastError } = useToast();

  function resetAndClose() {
    setDeleteTraceOnly(false);
    setDeleteFull(false);
    onClose();
  }

  async function handleConfirm() {
    const scope: 'trace_only' | 'full' = deleteFull ? 'full' : 'trace_only';

    try {
      await deleteThread({ threadId, scope });
      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toastError(getErrorMessage(err, 'Erro ao excluir conversa'));
    }
  }

  const isConfirmDisabled = !deleteTraceOnly && !deleteFull;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetAndClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir conversa</AlertDialogTitle>
          <AlertDialogDescription>
            Selecione o que deseja excluir. Esta ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          {/* Trace only */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="delete-trace"
              checked={deleteTraceOnly}
              onCheckedChange={(checked) => setDeleteTraceOnly(checked === true)}
            />
            <Label htmlFor="delete-trace" className="cursor-pointer">
              Apagar trace de execução
            </Label>
          </div>

          {/* Full delete */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="delete-full"
              checked={deleteFull}
              onCheckedChange={(checked) => setDeleteFull(checked === true)}
            />
            <Label htmlFor="delete-full" className="cursor-pointer">
              Apagar conversa inteira
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetAndClose} disabled={isPending}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isConfirmDisabled || isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? 'Excluindo...' : 'Confirmar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
